import { Component, Input, OnInit, inject, OnDestroy, OnChanges, SimpleChanges } from '@angular/core'
import { EMPTY, Observable, combineLatest, Subject, BehaviorSubject, ReplaySubject } from 'rxjs'
import { catchError, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators'
import { StoredEntityStateService } from '../../../../entity-store-access/services/stored-entity-state.service'
import {
  StoredEntityService,
  EntityConfigService,
  JmeleonActionsPermissionService,
  NotificationService,
  Entity
} from '@dot-it/jml-lib'
import { root } from '@dot-it/jml-lib'

enum InternalState {
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

@Component({
  selector: 'jml-lib-code-wrapper',
  templateUrl: './code-wrapper.component.html',
  styleUrls: ['./code-wrapper.component.scss']
})
export class CodeWrapperComponentHTML implements OnInit, OnChanges, OnDestroy {
  @Input() syntax!: string
  @Input() fieldName!: string
  @Input() dtoType!: string
  @Input() entityId!: number
  @Input() refreshTrigger: Subject<[string, number]>

  codeAvailable$: Observable<boolean>
  code: string
  lastUpdate: string

  private destroyed$ = new Subject()
  private $internalState = new BehaviorSubject<InternalState>(InternalState.SUCCESS)
  internalState$ = this.$internalState.asObservable().pipe(distinctUntilChanged())

  private _isDirty = false
  private dirtyEntityId: number
  private updatedCode: string

  get isDirty() {
    return this._isDirty
  }
  set isDirty(value: boolean) {
    this._isDirty = value
    this.dirtyEntityId = this.entityId
  }

  private storedEntityWrapper = inject(StoredEntityStateService)
  private storedEntityService = inject(StoredEntityService)
  private notificationService = inject(NotificationService)
  private japs = inject(JmeleonActionsPermissionService)
  private entityConfigService = inject(EntityConfigService)

  fieldReadable = true
  fieldWritable = false
  entityReadOnly = false

  ngOnInit() {
    const isFieldEditable$ = this.entityConfigService
      .getConfig$(this.dtoType)
      .pipe(map((config) => <boolean>config.fields.find((obj) => obj.field === this.fieldName).editable))

    const requestedPermissions = [root.dto.$dtoType.$dtoField.read, root.dto.$dtoType.$dtoField.write]
    const permissions$ = this.japs
      .userHasPermissionForActions(requestedPermissions, { $dtoType: this.dtoType, $dtoField: this.fieldName })
      .pipe(
        map((permissions) => {
          return {
            read: permissions[root.dto.$dtoType.$dtoField.read],
            write: permissions[root.dto.$dtoType.$dtoField.write]
          }
        })
      )

    combineLatest([isFieldEditable$, permissions$])
      .pipe(
        take(1),
        tap(([isFieldEditable, permissions]) => {
          this.fieldReadable = permissions.read || permissions.write
          this.fieldWritable = isFieldEditable && permissions.write
        })
      )
      .subscribe()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entityId) {
      this.onNewEntity()
    }
  }

  onNewEntity() {
    this.codeAvailable$ = this.storedEntityService.getEntity$(this.dtoType, this.entityId).pipe(
      filter((entity) => {
        if (!this._isDirty) {
          // no update tiggered this event
          return true
        }
        const newEntity = this.entityId !== this.dirtyEntityId
        return newEntity || this.updatedCode === entity.fields[this.fieldName]
      }),
      map((entity) => {
        this.dirtyEntityId = undefined
        this.updatedCode = undefined
        this.lastUpdate = entity.fields['_lastUpdate_']
        this.entityReadOnly = entity.fields['_readOnly_']
        this.$internalState.next(InternalState.SUCCESS)
        this.code = entity.fields[this.fieldName]

        return true
      }),
      catchError(() => EMPTY) // In case of error, complete the stream without emitting
    )
  }

  updateEntity(updatedCode: string) {
    this.updatedCode = updatedCode
    const updateField = { [this.fieldName]: updatedCode }

    this.$internalState.next(InternalState.LOADING)
    this.storedEntityService
      .updateEntityV2$(this.dtoType, this.entityId, updateField, this.lastUpdate)
      .pipe(
        tap((_) => this.refreshTrigger?.next([this.dtoType, this.entityId])),
        takeUntil(this.destroyed$)
      )
      .subscribe(
        // Success callback
        () => {
          setTimeout(() => {
            this.$internalState.next(InternalState.SUCCESS)
          }, 0)
        },
        // Error callback
        (error) => {
          this.updatedCode = undefined
          setTimeout(() => {
            this.$internalState.next(InternalState.ERROR)
          }, 0)
          console.error('CodeWrapperComponent.updateEntity: Cold not save code.', error)
        }
      )
  }

  getIcon(internalState: InternalState): string {
    switch (internalState) {
      case InternalState.LOADING:
        return 'pi pi-spin pi-spinner'
      case InternalState.ERROR:
        return 'pi pi-exclamation-triangle'
      case InternalState.SUCCESS:
        return 'pi pi-save'
      default:
        return 'pi pi-exclamation-triangle'
    }
  }

  ngOnDestroy() {
    this.destroyed$.next()
    this.destroyed$.complete()
  }
}
